import {
	QuantitiesByLocation,
	StockVariantApi,
	StockItemApi,
	ShopPublicInfo,
	PricingItem,
} from 'common/types';
import { getShopLocationsWithContact } from 'common/utils/shopUtils';

export const getStockItemLocation = (
	quantitiesByLocation: QuantitiesByLocation,
	shopPublicInfo: ShopPublicInfo,
) => {
	const locationId = Object.keys(quantitiesByLocation)[0];
	const locations = getShopLocationsWithContact(shopPublicInfo);
	const correctLocation = locations.find((location) => location.id === locationId);
	const locationName = correctLocation ? correctLocation.name : '';
	return locationName;
};

export const getStockItemLocationName = (id: string, shopPublicInfo: ShopPublicInfo) => {
	const locations = getShopLocationsWithContact(shopPublicInfo);
	const correctLocation = locations.find((location) => location.id === id);
	const name = correctLocation ? correctLocation.name : '';
	return name;
};

export const getNewQuantitiesByLocationOnBulkQuantityChange = (
	locationId: string,
	stockVariant: StockVariantApi,
	quantity: number | null,
) => {
	return {
		...stockVariant.quantitiesByLocation,
		[locationId]: quantity,
	};
};

export const calculateNewTotalFromQuantitiesByLocation = (
	quantitiesByLocation: QuantitiesByLocation,
) => {
	const keys = Object.keys(quantitiesByLocation);
	if (keys.length) {
		let newTotal: number | null = 0;
		keys.forEach((key) => {
			const qty = quantitiesByLocation[key];
			if (qty !== null && newTotal !== null) {
				newTotal += qty;
			} else {
				newTotal = null;
			}
		});
		return newTotal;
	} else {
		return undefined;
	}
};

export const getUpdatedVariantQuantitiesByLocationOnItemLocationChange = (
	oldLocationId: string,
	newLocationId: string,
	stockVariant: StockVariantApi,
) => {
	if (oldLocationId === newLocationId) {
		return stockVariant.quantitiesByLocation;
	}
	const qty = stockVariant.quantitiesByLocation;
	const oldLocationQty = qty[oldLocationId];
	const newLocationQty = qty[newLocationId];
	const updOldLocationQty = oldLocationQty ? Math.max(oldLocationQty - 1, 0) : 0;
	const updNewLocationQty = (newLocationQty ?? 0) + 1;
	return {
		...qty,
		[oldLocationId]: updOldLocationQty,
		[newLocationId]: updNewLocationQty,
	};
};

export const getUpdatedVariantQuantitiesByLocation = (
	stockItemLocationId: string,
	stockVariant: StockVariantApi,
	amountToAdd: number,
) => {
	const existingQuantity = stockVariant.quantitiesByLocation[stockItemLocationId] ?? 0;
	const newQuantity = Math.max(existingQuantity + amountToAdd, 0);
	return {
		...stockVariant.quantitiesByLocation,
		[stockItemLocationId]: newQuantity,
	};
};

export const getUpdatedVariantQuantitiesByLocationOnMassDelete = (
	stockVariant: StockVariantApi | undefined,
	itemsToDeleteForVariant: StockItemApi[],
) => {
	const initialQtyByLocation: QuantitiesByLocation = stockVariant?.quantitiesByLocation ?? {};
	const newQuantitiesByLocation = itemsToDeleteForVariant.reduce((qtyByLoc, item) => {
		const itemLocationId: string | null = Object.keys(item.quantitiesByLocation ?? {})[0];
		if (itemLocationId) {
			const currentQuantity = qtyByLoc[itemLocationId];
			return {
				...qtyByLoc,
				[itemLocationId]: currentQuantity ? currentQuantity - 1 : 0,
			};
		}
		return qtyByLoc;
	}, initialQtyByLocation);
	return newQuantitiesByLocation;
};

export const getUpdatedVariantQtyByLocOnBulkToItemChange = (stockVariant: StockVariantApi) => {
	/* When updating inventory type from bulk to item: 
        - all "nulls" in quantitiesByLocation and quantity should be 0 since individual stockType cannot have unlimited availability. 
    */
	const oldQtyByLoc = stockVariant.quantitiesByLocation ?? {};
	const oldQtyByLocKeys = Object.keys(oldQtyByLoc);
	const newQtyByLoc: QuantitiesByLocation = {};
	oldQtyByLocKeys.forEach((locationId) => {
		if (oldQtyByLoc[locationId] === null) {
			newQtyByLoc[locationId] = 0;
		} else {
			newQtyByLoc[locationId] = oldQtyByLoc[locationId];
		}
	});
	return newQtyByLoc;
};

export const getVariantQtyFromQtyByLoc = (qtyByLoc: QuantitiesByLocation) => {
	const quantities = Object.values(qtyByLoc);
	let updatedQuantity: number | null = 0;
	if (quantities.includes(null)) {
		updatedQuantity = null;
	} else {
		updatedQuantity = (quantities as number[]).reduce((acc, value) => acc + value, 0);
	}
	return updatedQuantity;
};

export const getItemsWithBasePrice = (items: PricingItem[], basePrice: number) => {
	return items.map((item) => ({
		...item,
		price: (item.multiplier || 0) * basePrice,
	}));
};
